import {
  DESTINATION_SERVICE,
  PROPERTY_SERVICE,
  SEARCH_SERVICE,
  LA_DOC_SERVICE,
} from '@damascus-components/DataDog/Datadog.constants';
import {datadogLogs} from '@datadog/browser-logs';

export type Options =
  | {
      cache?: 'no-store';
      headers?: Record<string, string>;
      method?: 'GET';
    }
  | {
      cache?: 'no-store';
      headers?: Record<string, string>;
      method: 'POST';
      body: string;
    };

type Req = {
  abort: Function;
  url: string;
  options: RequestInit;
};

const OptionsDefault = {
  method: 'GET' as const,
  headers: {'Content-Type': 'application/json'},
};

export class HttpRequest<T> {
  private request: Req = {
    abort: () => {},
    url: '',
    options: {method: 'GET'},
  };

  constructor(URL: string, opts: Options = OptionsDefault) {
    this.request.url = this.getSafeURL(URL);
    this.request.options = this.getFetchOptions(opts);
    const {signal, abort} = new AbortController();
    this.request.options.signal = signal;
    this.request.abort = abort;
  }

  private getSafeURL(url: string): string {
    switch (true) {
      case url.startsWith(PROPERTY_SERVICE):
        return url;
      case url.startsWith(SEARCH_SERVICE):
        return url;
      case url.startsWith(DESTINATION_SERVICE):
        return url;
      case url.startsWith(LA_DOC_SERVICE):
        return url;
      default:
        throw new Error('Invalid URL: The domain is not allowed.');
    }
  }

  private getHeaders(headers: Record<string, string> = {}): Headers {
    const newHeaders = new Headers({'Content-Type': 'application/json'});
    if (headers) {
      Object.entries(headers).forEach(([key, value]) => {
        newHeaders.append(key, value);
      });
    }
    return newHeaders;
  }

  private getFetchOptions(options: Options): RequestInit {
    const fetchOptions: RequestInit = {};
    fetchOptions.headers = this.getHeaders(options?.headers);
    fetchOptions.method = options?.method ?? 'GET';
    if (options.method === 'POST') {
      fetchOptions.body = options.body;
    }
    if (options?.cache) {
      fetchOptions.cache = options.cache;
    } else {
      fetchOptions.next = {revalidate: 300};
    }
    return fetchOptions;
  }

  public async send(): Promise<T> {
    const {url, options} = this.request;
    try {
      let resp = await fetch(url, options);
      if (!resp.ok) {
        const message = resp.statusText ?? 'Network Error! Please try later';
        throw new Error(message);
      }
      if (resp?.json) {
        resp = await resp.json();
      }
      return resp as T;
    } catch (err: any) {
      const message =
        err?.message ?? `Request [${options.method}] - ${url} Failed`;
      const error = new Error(message, {cause: err});
      throw error;
    }
  }

  public abort() {
    if (this?.request?.abort) {
      this.request.abort();
    }
  }
}
